import { useState, useCallback } from 'react';
import { convertRateToMonthlyNumber } from 'utils/convertRateToMonthly';
import { formatNumber } from 'utils/formatNumber';
import { getAge } from 'utils/getDataLayerElementsHelper';
import { AdditionalIncomeSectionHeading } from 'views/employment/AdditionalIncomeSectionHeading';
import {
  yesNoOptions,
  employmentStatusOptionsAU,
  unemploymentStatusOptionsAU,
  additionalIncomeTypeGroupsAU,
  numbersOnlyPattern,
  UNEMPLOYED,
  GOVERNMENT_BENEFITS,
  WORKERS_COMPENSATION,
  auNonGovIndustryOptions,
  OTHER,
  unemployedLengthLabels,
} from '../_constants';
import { validEmployerNamePattern } from './constants';

const incomeValidationMessages = {
  required: 'Please enter your take-home pay.',
  frequencyRequired: 'Please select a frequency.',
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters',
  invalid: 'Please enter a whole dollar amount, without cents.',
};

const additionalIncomeValidationMessages = {
  required: 'Please enter your additional income amount.',
  frequencyRequired: 'Please select a frequency.',
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters',
  invalid: 'Please enter a whole dollar amount, without cents.',
};

const lengthOfTimeAtCurrentEmployerMessages = {
  required: 'Please enter your length of time employed.',
  invalid: 'Please enter numbers only.',
  monthsError: 'Please enter the number of months at your current employer. If less than 1 month, enter 0.',
  yearsError: 'Please enter your length of time employed.',
  monthRangeError: 'Please provide a number between {{minMonths}}-{{maxMonths}}.',
  yearRangeError: 'Please provide a number between {{minYears}}-{{maxYears}}.',
  excessAgeError: 'The length of time looks too high. Please review your input.',
};

const lengthOfTimeUnemployedMessages = {
  required: 'Please enter the length of time you have been in this situation.',
  invalid: 'Please enter numbers only.',
  monthsError: 'Please enter the number of months you have been in this situation. If less than 1 month, enter 0.',
  yearsError: 'Please enter the length of time you have been in this situation.',
  monthRangeError: 'Please provide a number between {{minMonths}}-{{maxMonths}}.',
  yearRangeError: 'Please provide a number between {{minYears}}-{{maxYears}}.',
  excessAgeError: 'The length of time looks too high. Please review your input.',
};

const lengthOfTimeGovernmentBenefitMessages = {
  required: `Please enter the length of time you've received your current government benefits.`,
  invalid: 'Please enter numbers only.',
  monthsError: `Please enter the number of months you've received your current government benefits. If less than 1 month, enter 0.`,
  yearsError: `Please enter the number of years you've received your current government benefits.`,
  monthRangeError: 'Please provide a number between {{minMonths}}-{{maxMonths}}.',
  yearRangeError: 'Please provide a number between {{minYears}}-{{maxYears}}.',
  excessAgeError: 'The length of time looks too high. Please review your input.',
};

const lengthOfTimeWorkersCompensationMessages = {
  required: `Please enter the length of time of years you have received your current compensation`,
  invalid: 'Please enter numbers only.',
  monthsError: `Please enter the number of months you have received your current compensation. If less than 1 month, enter 0.`,
  yearsError: `Please enter the number of years you have received your current compensation.`,
  monthRangeError: 'Please provide a number between {{minMonths}}-{{maxMonths}}.',
  yearRangeError: 'Please provide a number between {{minYears}}-{{maxYears}}.',
  excessAgeError: 'The length of time looks too high. Please review your input.',
};

const employerNameMessages = {
  required: 'Please provide us the name of the company you work at.',
  invalid: `Company name contains an invalid character. Must only contain letters, numbers and these characters: ( & , - . : ; ' " ! @ # $ % ^ & * () _ + = ? /.' )`,
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters',
};

export const employmentDetails = [
  {
    component: 'Select',
    name: 'employmentStatus',
    label: 'Primary income type',
    options: employmentStatusOptionsAU,
    testId: 'employment-status',
    validate: {
      messages: {
        required: 'Please provide us your employment status.',
      },
    },
  },
  {
    condition: values => !!values.employmentStatus,
    name: 'hasEmploymentStatus',
    fields: [
      {
        condition: values => UNEMPLOYED === values.employmentStatus,
        component: 'Select',
        name: 'incomeSource',
        label: 'What best describes your situation?',
        testId: 'income-source',
        options: unemploymentStatusOptionsAU,
        validate: {
          messages: {
            required: 'Please select an option that best describes your situation',
          },
        },
      },
      {
        condition: values =>
          ![UNEMPLOYED].includes(values.employmentStatus) ||
          values.incomeSource === WORKERS_COMPENSATION ||
          values.incomeSource === OTHER,
        component: 'Select',
        name: 'industry',
        label: 'Employment Industry',
        options: auNonGovIndustryOptions,
        testId: 'industry',
        validate: {
          messages: {
            required: 'Please provide us the industry you work in.',
          },
        },
      },
      {
        condition: values =>
          ![UNEMPLOYED].includes(values.employmentStatus) ||
          values.incomeSource === WORKERS_COMPENSATION ||
          values.incomeSource === OTHER,
        component: 'Input',
        name: 'employerName',
        label: 'Company name',
        testId: 'employer-name',
        validate: {
          props: {
            minLength: 1,
            maxLength: 30,
            validPattern: validEmployerNamePattern,
          },
          messages: employerNameMessages,
        },
      },
      {
        condition: values => UNEMPLOYED === values.employmentStatus && values.incomeSource,
        component: 'TimeSpan',
        name: 'lengthOfTimeAtCurrentEmployer',
        label: values => unemployedLengthLabels[values.incomeSource],
        testId: 'current-employer-length',
        validate: {
          props: storeState => {
            const age = getAge(storeState.acquisition.personalDetails.dateOfBirth, undefined, true);
            return {
              ageAsUpperLimit: age,
              minYears: 0,
              maxYears: 99,
              minMonths: 0,
              maxMonths: 11,
              validPattern: numbersOnlyPattern,
            };
          },
          messages: lengthOfTimeUnemployedMessages,
          component: 'TimeSpan',
        },
      },
      {
        condition: values => ![UNEMPLOYED, GOVERNMENT_BENEFITS, WORKERS_COMPENSATION].includes(values.employmentStatus),
        component: 'TimeSpan',
        name: 'lengthOfTimeAtCurrentEmployer',
        label: 'Length of time at current employer',
        testId: 'current-employer-length',
        validate: {
          props: storeState => {
            const age = getAge(storeState.acquisition.personalDetails.dateOfBirth, undefined, true);
            return {
              ageAsUpperLimit: age,
              minYears: 0,
              maxYears: 99,
              minMonths: 0,
              maxMonths: 11,
              validPattern: numbersOnlyPattern,
            };
          },
          messages: lengthOfTimeAtCurrentEmployerMessages,
          component: 'TimeSpan',
        },
      },
      {
        condition: values => GOVERNMENT_BENEFITS === values.employmentStatus,
        component: 'TimeSpan',
        name: 'lengthOfTimeAtCurrentEmployer',
        label: 'Length of time on benefits',
        testId: 'current-employer-length',
        validate: {
          props: storeState => {
            const age = getAge(storeState.acquisition.personalDetails.dateOfBirth, undefined, true);
            return {
              ageAsUpperLimit: age,
              minYears: 0,
              maxYears: 99,
              minMonths: 0,
              maxMonths: 11,
              validPattern: numbersOnlyPattern,
            };
          },
          messages: lengthOfTimeGovernmentBenefitMessages,
          component: 'TimeSpan',
        },
      },
      {
        condition: values => WORKERS_COMPENSATION === values.employmentStatus,
        component: 'TimeSpan',
        name: 'lengthOfTimeAtCurrentEmployer',
        label: 'Length of time on compensation',
        testId: 'current-employer-length',
        validate: {
          props: storeState => {
            const age = getAge(storeState.acquisition.personalDetails.dateOfBirth, undefined, true);
            return {
              ageAsUpperLimit: age,
              minYears: 0,
              maxYears: 99,
              minMonths: 0,
              maxMonths: 11,
              validPattern: numbersOnlyPattern,
            };
          },
          messages: lengthOfTimeWorkersCompensationMessages,
          component: 'TimeSpan',
        },
      },
      {
        component: 'Frequency',
        name: 'income',
        label: 'Your take-home pay?',
        helpText: 'The amount you receive after tax and deductions',
        mode: 'select',
        amountPrefix: '$',
        selectPlaceholder: 'Frequency',
        quarterly: false,
        testId: 'income',
        validate: {
          props: { minLength: 1, maxLength: 6, validPattern: numbersOnlyPattern },
          messages: incomeValidationMessages,
          component: 'Frequency',
        },
      },
      {
        component: 'RadioTile',
        name: 'hasOtherIncome',
        label: 'Do you have additional income?',
        helpText: 'e.g. rental income, government benefits, gig work (Uber, DiDi)',
        testId: 'has-other-income',
        options: yesNoOptions,
        showCircles: true,
        validate: {
          messages: {
            required: 'Please select yes or no.',
          },
        },
      },
      {
        condition: values => values.hasOtherIncome === 'yes',
        name: 'otherIncomeGroup',
        fields: [
          {
            component: 'CustomComponent',
            name: 'additionalIncomeSectionHeading',
            children: AdditionalIncomeSectionHeading,
          },
          {
            component: 'GroupedSelect',
            name: 'otherEmploymentStatus',
            label: 'Additional income type',
            placeholder: 'Please select',
            groups: additionalIncomeTypeGroupsAU,
            testId: 'other-employment-status',
            validate: {
              messages: {
                required: 'Please select an additional income type.',
              },
            },
          },
          {
            component: 'Frequency',
            name: 'otherIncome',
            label: 'Additional income amount',
            helpText: 'The amount you receive',
            mode: 'select',
            amountPrefix: '$',
            selectPlaceholder: 'Frequency',
            quarterly: false,
            testId: 'other-income',
            validate: {
              props: { minLength: 1, maxLength: 6, validPattern: numbersOnlyPattern },
              messages: additionalIncomeValidationMessages,
              component: 'Frequency',
            },
          },
        ],
      },
    ],
  },
];

export const useWarning = () => {
  const [warnings, setWarning] = useState([]);

  const check = useCallback(values => {
    const additionMonthlyIncome = values.hasOtherIncome === 'yes' ? convertRateToMonthlyNumber(values.otherIncome) : 0;

    const totalMonthlyIncome = convertRateToMonthlyNumber(values.income) + additionMonthlyIncome;

    const isUnemployed = values.employmentStatus === UNEMPLOYED;
    const income0 = totalMonthlyIncome === 0;
    const incomeLessThan100 = totalMonthlyIncome !== 0 && totalMonthlyIncome < 100;
    const incomeGreaterThanOrEqual16k = totalMonthlyIncome >= 16000;

    const newWarnings = [];
    if (isUnemployed) {
      newWarnings.push(
        `Please review and confirm your employment status and your income is $${formatNumber(values.income.amount)}.`,
      );
    }
    if (income0) {
      newWarnings.push(`Please update your total income. It must be greater than $0.`);
    }
    if (incomeLessThan100) {
      newWarnings.push(
        `Please review and confirm that your total net monthly income is $${formatNumber(totalMonthlyIncome)}.
            (Please note, we've converted your income to a monthly figure).`,
      );
    }
    if (incomeGreaterThanOrEqual16k) {
      newWarnings.push(`The income you've entered works out at $${formatNumber(totalMonthlyIncome)}.`);
    }
    setWarning(newWarnings);
    return newWarnings.length === 0;
  }, []);

  return { check, warnings };
};
